import React from 'react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { Box, Paper, Typography, useTheme } from '@mui/material';

/**
 * A styled pie chart component with consistent styling
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - The data array for the chart
 * @param {String} props.dataKey - The key to use for the values
 * @param {String} props.nameKey - The key to use for the labels
 * @param {String} props.title - Optional chart title
 * @param {String} props.subtitle - Optional chart subtitle
 * @param {Number} props.height - Chart height in pixels (default: 300)
 * @param {Array} props.colors - Array of colors to use for the pie slices
 * @param {Boolean} props.isLoading - Loading state
 * @param {Boolean} props.showEmptyState - Whether to show empty state when no data
 * @param {String} props.emptyStateMessage - Message to show in empty state
 */
const StyledPieChart = ({
  data = [],
  dataKey = 'value',
  nameKey = 'name',
  title,
  subtitle,
  height = 300,
  colors,
  isLoading = false,
  showEmptyState = true,
  emptyStateMessage = 'No data available'
}) => {
  const theme = useTheme();
  const defaultColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
    '#8884d8',
    '#82ca9d',
    '#ffc658',
    '#ff8042'
  ];
  
  const pieColors = colors || defaultColors;
  const hasData = data && data.length > 0;

  const renderCustomizedLabel = React.useCallback(({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  }, []);

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        height: '100%', 
        borderRadius: 3,
        border: '1px solid rgba(0, 0, 0, 0.05)'
      }}
    >
      {title && (
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600, 
            mb: subtitle ? 0.5 : 2
          }}
        >
          {title}
        </Typography>
      )}
      
      {subtitle && (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ mb: 2 }}
        >
          {subtitle}
        </Typography>
      )}
      
      {isLoading ? (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height={height}
        >
          <Typography variant="body1" color="text.secondary">
            Loading chart data...
          </Typography>
        </Box>
      ) : !hasData && showEmptyState ? (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height={height}
        >
          <Typography variant="body1" color="text.secondary">
            {emptyStateMessage}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', height }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey={dataKey}
                nameKey={nameKey}
              >
                {data.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={pieColors[index % pieColors.length]} 
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 8,
                  boxShadow: theme.shadows[2],
                }}
                formatter={(value, name) => [`${value}`, name]}
              />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="center"
                wrapperStyle={{ 
                  paddingTop: 20,
                  fontSize: 12,
                  color: theme.palette.text.secondary
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default StyledPieChart;